import 'ng-table';
import 'ui-select';
import {State, Module} from 'app/decorators';
import Modal from 'app/services/modals';
import Config from 'app/services/config';
import BaseRoute from 'app/routing';
import {ListCtrl, DetailsCtrl, OrderDetailsCtrl, FilesCtrl, NotesCtrl, LogCtrl} from './ctrls';
import BillingCtrl from './billing.ctrls';

import OrderStore from 'app/stores/order';
import CustomerStore from 'app/stores/customer';
import ApplicatorConfigStore from 'app/stores/applicator.config';
import CompanyFreightStore from 'app/stores/company.freight';
import NutrientPricesStore from 'app/stores/nutrient.price';
import FileStore from 'app/stores/file';
import PrinterStore from 'app/stores/printer';

import FadeOut from 'app/directives/fade.out';

import BlndDatePicker from 'app/directives/blnd.datepicker';
import BlndDateRangePicker from '../directives/blnd.daterangepicker';
import CustomerPicker from 'app/directives/customer.picker';
import ApplicatorPicker from 'app/directives/applicator.picker';
import ShipToPicker from 'app/directives/ship.to.picker';
import CropPicker from 'app/directives/crop.picker';
import CompanyPicker from 'app/directives/company.picker';
import NutrientsComponent from './nutrients.component';
import ProductsComponent from 'app/directives/products.component';
import OrderStatus from 'app/directives/order.status';
import OrderList from './components/list';
import OrderPriorityList from './components/priority/main';
import BillingComponent from 'app/directives/billing/component';
import DeliveryPicker from 'app/directives/delivery.picker';
import PriceTableComponent from 'app/directives/price.section';
import UploadComponent from 'app/directives/upload.section';
import Activity from 'app/directives/activity';
import StatusButton from 'app/directives/status.button/component';
import HitPercentageButton from 'app/directives/hit-percentage.button/component';
import PrintButton from 'app/directives/print.button/component';
import ChemicalProgramComponent from 'app/directives/chemical.program.picker';
import AcresControlComponent from 'app/directives/acres.calc.control';

import SecureUrlService from 'app/services/secure.url';

// Templates
import listTpl from './templates/list.html';
import detailTpl from './templates/detail.html';
import detailOrderTpl from './templates/detail.order.html';
import filesTpl from './templates/detail.files.html';
import notesTpl from './templates/detail.notes.html';
import logTpl from './templates/detail.log.html';
import billingTpl from './templates/detail.billing.html';
import actionsTpl from './templates/actions.html';


@Module({
    name: 'app.orders',
    modules: [
        BaseRoute,
        Modal,
        Config,
        OrderStore,
        CustomerStore,
        FileStore,
        ApplicatorConfigStore,
        CompanyFreightStore,
        NutrientPricesStore,
        NutrientsComponent,
        ProductsComponent,
        PriceTableComponent,
        UploadComponent,
        CropPicker,
        ShipToPicker,
        BlndDatePicker,
        BlndDateRangePicker,
        CustomerPicker,
        DeliveryPicker,
        ApplicatorPicker,
        CompanyPicker,
        OrderStatus,
        OrderList,
        BillingComponent,
        AcresControlComponent,
        OrderPriorityList,
        'ngFileUpload',
        SecureUrlService,
        'selector',
        Activity,
        StatusButton,
        HitPercentageButton,
        PrintButton,
        PrinterStore,
        ChemicalProgramComponent,
        FadeOut,
    ],
})
@State({
    name: 'r.in.top.orders',
    url: '/orders',
    abstract: '.list',
    views: {
        '': {
            template: '<ui-view></ui-view>',
        },
        'actions': {
            template: actionsTpl,
        }
    },
    ncyBreadcrumb: {
        label: 'Orders',
    },
})
@State({
    name: 'r.in.top.orders.list',
    abstract: '.main',
    controller: ListCtrl,
    template: listTpl,
    ncyBreadcrumb: {
        skip: true,
    },
})
@State({
    name: 'r.in.top.orders.list.priority',
    url: '/sorted',
    template: '<order-priority-list></order-priority-list>',
    data: {
        permissions: {
            only: [
                'main.can_schedule_orders',
            ],
        },
    },
    ncyBreadcrumb: {
        skip: true,
    },
})
@State({
    name: 'r.in.top.orders.list.main',
    url: '?company&search&created_at__range&status&page&ordering&contains_product',
    // The React page owns these query params (it writes them directly and
    // refetches itself); the legacy fallback also drives them via UI + an
    // explicit `$state.go('.', ..., {notify: false})`. Neither relies on the
    // state reloading when they change, so disable it - otherwise Angular
    // remounts the React island (and reconciles a stale `$location`) on every
    // paginate/filter, causing flicker and the "click navbar twice" bug.
    reloadOnSearch: false,
    // Feature flag managed by the backend controls the React list rollout.
    templateProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_ORDERS_LIST');
        return useReact
            ? `<div react-view="orders-list"></div>`
            : `<order-list extra-fields="['customer']"></order-list>`;
    },
    ncyBreadcrumb: {
        skip: true,
    },
})
@State({
    name: 'r.in.top.orders.detail',
    url: '/{orderId:int}',
    controller: DetailsCtrl,
    template: detailTpl,
    as: 'vmo',
    resolve: {
        $order: function($stateParams, OrderStore) {
            return OrderStore.get($stateParams.orderId);
        },
    },
    ncyBreadcrumb: {
        label: 'Order #{{ vmo.order.order_id }}',
        parent: 'r.in.top.orders',
    },
    abstract: '.order',
})
@State({
    name: 'r.in.top.orders.detail.order',
    url: '',
    controller: OrderDetailsCtrl,
    template: detailOrderTpl,
    ncyBreadcrumb: {
        label: 'Details',
    },
    params: {
        created: null,
    }
})
@State({
    name: 'r.in.top.orders.detail.files',
    url: '/files',
    controller: FilesCtrl,
    template: filesTpl,
    ncyBreadcrumb: {
        label: 'Files',
    },
})
@State({
    name: 'r.in.top.orders.detail.notes',
    url: '/notes',
    controller: NotesCtrl,
    template: notesTpl,
    ncyBreadcrumb: {
        label: 'Notes',
    },
})
@State({
    name: 'r.in.top.orders.detail.log',
    url: '/log',
    controller: LogCtrl,
    template: logTpl,
    ncyBreadcrumb: {
        label: 'Log',
    },
})
@State({
    name: 'r.in.top.orders.detail.billing',
    url: '/billing',
    controller: BillingCtrl,
    template: billingTpl,
    ncyBreadcrumb: {
        label: 'Billing',
    },
    resolve: {},
})
export default class States { }



// WEBPACK FOOTER //
// ./src/app/orders/routing.js